import { SuperOpsStateManager } from "./SuperOpsStateManager";
import { cache, ZomentumStorageKeys } from "@/Contracts";
import { isEmbedded } from "@/Utils/superops/embedDetection";
// import { isAllowedEmbedRoute, getEmbedErrorRoute } from '../utils/EmbedDetection';
import { SUPEROPS_CONFIG } from "../config/SuperOpsConfig";
import { prependBasePath } from "@/Utils/routing/getAppPathname";
import {
	SuperOpsState,
	SuperOpsDataResponse,
	SuperOpsInboundMessage,
	SuperOpsOutboundMessage,
	SuperOpsRestoreMessage
} from "./interfaces";

class SuperOpsMessageService {
	private static instance: SuperOpsMessageService;
	private stateManager: SuperOpsStateManager;
	private isInitialized = false;

	private constructor() {
		this.stateManager = new SuperOpsStateManager();
	}

	static getInstance(): SuperOpsMessageService {
		if (!SuperOpsMessageService.instance) {
			SuperOpsMessageService.instance = new SuperOpsMessageService();
		}
		return SuperOpsMessageService.instance;
	}

	initialize(): void {
		if (this.isInitialized) return;

		// Only initialize if we're in embedded context
		if (!isEmbedded()) return;

		this.listenForMessages();
		this.checkForPendingRestoration();
		this.isInitialized = true;
	}

	private listenForMessages(): void {
		window.addEventListener(
			"message",
			this.handleInboundMessage.bind(this)
		);
	}

	private async handleInboundMessage(event: MessageEvent): Promise<void> {
		if (!event.data || typeof event.data !== "object") return;

		const message = event.data as SuperOpsInboundMessage;

		try {
			switch (message.type) {
				case SUPEROPS_CONFIG.MESSAGE_TYPES.RESTORE_STATE:
					await this.handleRestoreState(
						(message as SuperOpsRestoreMessage).restoreState
					);
					break;

				case SUPEROPS_CONFIG.MESSAGE_TYPES.FETCH_DATA:
					await this.handleFetchData(message.requestId);
					break;

				default:
					// Ignore unknown message types
					break;
			}
		} catch (error) {
			console.error("Error handling SuperOps message:", error);
		}
	}

	private async handleRestoreState(
		restoreState: SuperOpsState
	): Promise<void> {
		try {
			// Check if route is allowed in embedded mode
			// if (!isAllowedEmbedRoute(restoreState.route)) {
			//   await this.navigateToRoute(getEmbedErrorRoute());
			//   return;
			// }

			const isAuthenticated = await this.checkAuthenticationStatus();

			if (!isAuthenticated) {
				// Save state for after login and redirect to login
				this.stateManager.saveRestoreStateForLater(restoreState);
				await this.navigateToLogin();
				return;
			}

			// User is authenticated and route is allowed - restore state immediately
			await this.stateManager.restoreState(restoreState);
		} catch (error) {
			console.error("Failed to handle restore state:", error);
		}
	}

	private async handleFetchData(requestId: string): Promise<void> {
		try {
			const isAuthenticated = await this.checkAuthenticationStatus();

			if (!isAuthenticated) {
				console.warn(
					"SuperOps fetch data failed: User not authenticated"
				);
				this.sendDataResponse(requestId, {} as SuperOpsState);
				return;
			}

			const data = await this.gatherCurrentData();
			this.sendDataResponse(requestId, data);
		} catch (error) {
			console.error("Failed to fetch data:", error);
			this.sendDataResponse(requestId, {} as SuperOpsState);
		}
	}

	private async checkAuthenticationStatus(): Promise<boolean> {
		try {
			const authToken = cache.get(ZomentumStorageKeys.Token);
			return !!authToken;
		} catch (error) {
			return false;
		}
	}

	private async navigateToLogin(): Promise<void> {
		const loginUrl = prependBasePath("/login") + "?embed=true";
		window.history.pushState(null, "", loginUrl);
		const event = new PopStateEvent("popstate");
		window.dispatchEvent(event);
	}

	// private async navigateToRoute(route: string): Promise<void> {
	//   const routeWithEmbed = route.includes('?') ? `${route}&embed=true` : `${route}?embed=true`;
	//   window.history.pushState(null, '', routeWithEmbed);
	//   const event = new PopStateEvent('popstate');
	//   window.dispatchEvent(event);
	// }

	private checkForPendingRestoration(): void {
		// Check if user just logged in and we have pending restoration
		this.stateManager.checkForPendingRestoration();
	}

	private async gatherCurrentData(): Promise<SuperOpsState> {
		return this.stateManager.getCurrentState();
	}

	private sendDataResponse(
		requestId: string,
		data: SuperOpsDataResponse["data"]
	): void {
		this.sendMessage({
			type: SUPEROPS_CONFIG.MESSAGE_TYPES.DATA_RESPONSE,
			requestId,
			data
		});
	}

	openClientInNewTab(clientId: string): void {
		if (!isEmbedded()) {
			return;
		}

		this.sendMessage({
			type: SUPEROPS_CONFIG.MESSAGE_TYPES.OPEN_CLIENT_IN_NEW_TAB,
			clientId
		});
	}

	private sendMessage(message: SuperOpsOutboundMessage): void {
		if (window.parent && window.parent !== window.self) {
			try {
				window.parent.postMessage(message, "*");
			} catch (error) {
				console.error("Failed to send message to parent:", error);
			}
		}
	}
}

export { SuperOpsMessageService };
export type {
	SuperOpsState,
	SuperOpsDataResponse,
	SuperOpsInboundMessage,
	SuperOpsOutboundMessage
};
